import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditsAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Coins,
  Zap,
  Crown,
  Sparkles,
  Brain,
  FileText,
  CreditCard,
  ArrowRight,
  Check,
  Star,
  RefreshCw,
  ExternalLink,
  Loader2,
  Clock,
  Gift,
  Heart,
  Users,
  FolderHeart,
  ArrowLeft
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const PatientCredits = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState(null);
  const [packs, setPacks] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [purchasing, setPurchasing] = useState(false);
  const [purchaseReference, setPurchaseReference] = useState(null);
  const [pollingInterval, setPollingIntervalState] = useState(null);

  useEffect(() => {
    loadData();
    return () => {
      if (pollingInterval) clearInterval(pollingInterval);
    };
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [balanceRes, packsRes, transRes] = await Promise.all([
        creditsAPI.getBalance(),
        creditsAPI.getPacks(),
        creditsAPI.getTransactions()
      ]);
      setBalance(balanceRes.data);
      setPacks(packsRes.data.packs);
      setTransactions(transRes.data.transactions);
    } catch (error) {
      console.error('Error loading credits data:', error);
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const startPurchasePolling = useCallback((reference) => {
    const interval = setInterval(async () => {
      try {
        const response = await creditsAPI.checkPurchaseStatus(reference);
        if (response.data.status === 'confirmed') {
          clearInterval(interval);
          setPollingIntervalState(null);
          setPurchaseReference(null);
          toast.success(`${response.data.credits} crédits ajoutés à votre compte !`);
          loadData();
        }
      } catch (error) {
        console.error('Error checking purchase status:', error);
      }
    }, 5000);
    setPollingIntervalState(interval);
  }, []);

  const handlePurchase = async (packKey) => {
    setPurchasing(true);
    try {
      const response = await creditsAPI.purchase({ pack: packKey });
      const { reference, chariow_url } = response.data;
      
      setPurchaseReference(reference);
      toast.info('Redirection vers Chariow en cours...');
      
      // Try to open in new tab, fallback to redirect
      const newWindow = window.open(chariow_url, '_blank');
      
      if (!newWindow || newWindow.closed || typeof newWindow.closed === 'undefined') {
        // Popup blocked - redirect in same window
        toast.warning('Popup bloqué - redirection directe...');
        setTimeout(() => {
          window.location.href = chariow_url;
        }, 1000);
      } else {
        // New tab opened successfully, start polling
        startPurchasePolling(reference);
      }
      
    } catch (error) {
      console.error('Error initiating purchase:', error);
      toast.error('Erreur lors de la création de la commande');
    } finally {
      setPurchasing(false);
    }
  };

  const getPackIcon = (key) => {
    const icons = {
      decouverte: Zap,
      standard: Star,
      pro: Crown,
      elite: Sparkles
    };
    return icons[key] || Coins;
  };

  const getPackColor = (key) => {
    const colors = {
      decouverte: 'from-blue-500 to-cyan-500',
      standard: 'from-purple-500 to-pink-500',
      pro: 'from-amber-500 to-orange-500',
      elite: 'from-emerald-500 to-teal-500'
    };
    return colors[key] || 'from-gray-500 to-gray-600';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-[#E53935]" />
      </div>
    );
  }

  const aiUsed = balance?.ai_questions_used_this_month || 0;
  const aiFree = balance?.ai_questions_free || 10;
  const aiRemaining = balance?.ai_questions_remaining_free ?? (aiFree - aiUsed);
  const docsFree = balance?.documents_free || 10;
  const docsCount = balance?.documents_count || 0;

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto" data-testid="patient-credits-page">
      {/* Back Button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate('/dashboard')}
        className="mb-4"
      >
        <ArrowLeft className="w-4 h-4 mr-2" />
        Retour au tableau de bord
      </Button>

      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 flex items-center gap-3">
              <Heart className="w-8 h-8 text-[#E53935]" />
              Mes Crédits
            </h1>
            <p className="text-slate-500 mt-1">Gérez vos crédits pour l&apos;IA et les documents médicaux</p>
          </div>
          <Button onClick={loadData} variant="outline" size="sm">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualiser
          </Button>
        </div>

        {/* Balance Card */}
        <Card className="bg-gradient-to-br from-[#1976D2] to-[#7C3AED] text-white border-0">
          <CardContent className="p-6">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
              <div>
                <p className="text-white/70 text-sm mb-1">Solde actuel</p>
                <div className="flex items-baseline gap-2">
                  <p className="text-4xl font-bold">{balance?.balance || 0}</p>
                  <span className="text-white/70">crédits</span>
                </div>
              </div>
              
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center">
                <Coins className="w-8 h-8" />
              </div>
            </div>

            {/* AI Questions Progress */}
            <div className="mt-4 pt-4 border-t border-white/20">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="flex items-center gap-2 text-white/80">
                  <Brain className="w-4 h-4" />
                  Questions IA gratuites ce mois
                </span>
                <span className="font-medium">
                  {aiRemaining === -1 
                    ? 'Illimitées' 
                    : `${aiRemaining} / ${aiFree}`
                  }
                </span>
              </div>
              {aiFree !== -1 && (
                <Progress 
                  value={Math.max(0, (aiRemaining / aiFree) * 100)} 
                  className="h-2 bg-white/20"
                />
              )}
            </div>

            {/* Documents Progress */}
            <div className="mt-3">
              <div className="flex items-center justify-between text-sm mb-2">
                <span className="flex items-center gap-2 text-white/80">
                  <FileText className="w-4 h-4" />
                  Documents médicaux gratuits
                </span>
                <span className="font-medium">
                  {docsFree === -1 
                    ? 'Illimités' 
                    : `${Math.max(0, docsFree - docsCount)} / ${docsFree}`
                  }
                </span>
              </div>
              {docsFree !== -1 && (
                <Progress 
                  value={Math.max(0, ((docsFree - docsCount) / docsFree) * 100)} 
                  className="h-2 bg-white/20"
                />
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Polling indicator */}
      {purchaseReference && (
        <Card className="mb-6 border-purple-200 bg-purple-50">
          <CardContent className="p-4 flex items-center gap-3">
            <RefreshCw className="w-5 h-5 text-purple-600 animate-spin" />
            <div className="flex-1">
              <p className="text-sm font-medium text-purple-900">Vérification du paiement en cours...</p>
              <p className="text-xs text-purple-700">Référence: {purchaseReference}</p>
            </div>
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                const pack = packs.find(p => p.popular) || packs[0];
                window.open(pack.url + `?ref=${purchaseReference}`, '_blank');
              }}
            >
              <ExternalLink className="w-4 h-4 mr-1" />
              Ouvrir Chariow
            </Button>
          </CardContent>
        </Card>
      )}

      <Tabs defaultValue="info" className="space-y-6">
        <TabsList className="grid grid-cols-3 w-full max-w-md">
          <TabsTrigger value="info">Comment ça marche</TabsTrigger>
          <TabsTrigger value="packs">Acheter</TabsTrigger>
          <TabsTrigger value="history">Historique</TabsTrigger>
        </TabsList>

        {/* How it works */}
        <TabsContent value="info" className="mt-6">
          <div className="space-y-6">
            {/* Free tier explanation */}
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <Gift className="w-6 h-6 text-emerald-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-emerald-800 mb-2">Compte Patient Gratuit</h3>
                    <p className="text-emerald-700 mb-4">
                      En tant que patient, vous bénéficiez de fonctionnalités gratuites chaque mois :
                    </p>
                    <ul className="space-y-2">
                      <li className="flex items-center gap-2 text-emerald-700">
                        <Check className="w-4 h-4" />
                        <span><strong>10 questions IA</strong> gratuites par mois</span>
                      </li>
                      <li className="flex items-center gap-2 text-emerald-700">
                        <Check className="w-4 h-4" />
                        <span><strong>10 documents médicaux</strong> gratuits dans votre dossier</span>
                      </li>
                      <li className="flex items-center gap-2 text-emerald-700">
                        <Check className="w-4 h-4" />
                        <span>Recherche de professionnels <strong>illimitée</strong></span>
                      </li>
                      <li className="flex items-center gap-2 text-emerald-700">
                        <Check className="w-4 h-4" />
                        <span>Prise de rendez-vous <strong>gratuite</strong></span>
                      </li>
                    </ul>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Credit usage */}
            <div className="grid sm:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Brain className="w-5 h-5 text-purple-500" />
                    Assistant IA
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-900 mb-1">1 crédit</p>
                  <p className="text-sm text-slate-500 mb-3">par question (après vos 10 gratuites)</p>
                  <p className="text-xs text-slate-400">
                    L&apos;assistant IA vous aide à comprendre vos symptômes, 
                    préparer vos consultations et suivre vos traitements.
                  </p>
                </CardContent>
              </Card>
              
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-500" />
                    Documents médicaux
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-3xl font-bold text-slate-900 mb-1">5 crédits</p>
                  <p className="text-sm text-slate-500 mb-3">par document (après vos 10 gratuits)</p>
                  <p className="text-xs text-slate-400">
                    Stockez ordonnances, analyses, radios et autres documents 
                    dans votre dossier médical sécurisé.
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Family Health Record */}
            <Card className="border-blue-200 bg-blue-50">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                    <FolderHeart className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-blue-800 mb-2">Carnet de Santé Familial</h3>
                    <p className="text-blue-700 mb-3">
                      Gérez les dossiers médicaux de toute votre famille depuis un seul compte.
                    </p>
                    <div className="flex items-center gap-2 text-blue-800 font-medium mb-3">
                      <Users className="w-4 h-4" />
                      <span>50 crédits/mois - Documents illimités pour toute la famille</span>
                    </div>
                    <Button 
                      variant="outline" 
                      className="border-blue-300 text-blue-700 hover:bg-blue-100"
                      onClick={() => navigate('/family-members')}
                    >
                      Gérer ma famille
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Credit Packs */}
        <TabsContent value="packs" className="mt-6">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {packs.map((pack) => {
              const Icon = getPackIcon(pack.key);
              const gradient = getPackColor(pack.key);
              
              return (
                <Card 
                  key={pack.key} 
                  className={`relative overflow-hidden hover:shadow-lg transition-all ${pack.popular ? 'ring-2 ring-purple-500' : ''}`}
                >
                  {pack.popular && (
                    <div className="absolute top-0 right-0 bg-purple-500 text-white text-xs px-3 py-1 rounded-bl-lg font-medium">
                      Populaire
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center mb-3`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <CardTitle className="text-lg">{pack.name}</CardTitle>
                    <CardDescription className="text-xs">{pack.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="pb-2">
                    <div className="flex items-baseline gap-1 mb-2">
                      <span className="text-3xl font-bold text-slate-900">{pack.credits}</span>
                      <span className="text-slate-500 text-sm">crédits</span>
                    </div>
                    <p className="text-lg font-semibold text-[#E53935]">
                      {pack.price_fcfa.toLocaleString()} FCFA
                    </p>
                    <p className="text-xs text-slate-500">
                      {pack.price_per_credit} FCFA/crédit
                    </p>
                  </CardContent>
                  <CardFooter>
                    <Button 
                      onClick={() => handlePurchase(pack.key)}
                      disabled={purchasing}
                      className={`w-full bg-gradient-to-r ${gradient} hover:opacity-90`}
                    >
                      {purchasing ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          Acheter <ArrowRight className="w-4 h-4 ml-2" />
                        </>
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })}
          </div>

          {/* What credits can buy */}
          <Card className="mt-6 border-slate-200">
            <CardContent className="p-4">
              <h4 className="font-semibold text-slate-800 mb-3">Avec 50 crédits, vous pouvez :</h4>
              <div className="grid sm:grid-cols-3 gap-4 text-sm">
                <div className="flex items-center gap-2 text-slate-600">
                  <Brain className="w-4 h-4 text-purple-500" />
                  <span>50 questions IA</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <FileText className="w-4 h-4 text-blue-500" />
                  <span>10 documents médicaux</span>
                </div>
                <div className="flex items-center gap-2 text-slate-600">
                  <FolderHeart className="w-4 h-4 text-green-500" />
                  <span>1 mois de carnet familial</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Transaction History */}
        <TabsContent value="history" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Historique des transactions</CardTitle>
            </CardHeader>
            <CardContent>
              {transactions.length === 0 ? (
                <div className="text-center py-8">
                  <Clock className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                  <p className="text-slate-500">Aucune transaction</p>
                  <p className="text-sm text-slate-400">Vos achats et consommations apparaîtront ici</p>
                </div>
              ) : (
                <ScrollArea className="h-[400px]">
                  <div className="space-y-3">
                    {transactions.map((tx) => (
                      <div 
                        key={tx.id} 
                        className="flex items-center justify-between p-3 rounded-lg bg-slate-50"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                            tx.amount > 0 ? 'bg-emerald-100' : 'bg-red-100'
                          }`}>
                            {tx.type === 'purchase' ? (
                              <CreditCard className={`w-5 h-5 ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                            ) : tx.type === 'ai_usage' ? (
                              <Brain className="w-5 h-5 text-red-600" />
                            ) : tx.type === 'document' ? (
                              <FileText className="w-5 h-5 text-red-600" />
                            ) : (
                              <Coins className={`w-5 h-5 ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`} />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{tx.description}</p>
                            <p className="text-xs text-slate-500">
                              {format(new Date(tx.created_at), 'dd MMM yyyy HH:mm', { locale: fr })}
                            </p>
                          </div>
                        </div>
                        <span className={`font-bold ${tx.amount > 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                          {tx.amount > 0 ? '+' : ''}{tx.amount}
                        </span>
                      </div>
                    ))}
                  </div>
                </ScrollArea>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default PatientCredits;
